/**
 * Breadcrumb navigation presentation for the player page header
 *
 * Renders a horizontal bar with the navigation trail.
 * The first entry (section name) is clickable and navigates back.
 * The last entry (current page) is displayed as non-clickable text.
 * Supports up to 3 levels: section → player → similar player (SE-4025).
 * @module features/player-detail/components/Breadcrumb/BreadcrumbView
 */

import { ArrowLeftIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/** A displayable breadcrumb segment with a pre-built press handler */
interface BreadcrumbSegment {
  /** Display label for this segment */
  readonly label: string;
  /** Unique key for React list rendering */
  readonly key: string;
  /** Whether this is the last (current) segment — rendered as non-clickable */
  readonly isCurrent: boolean;
  /** Pre-built press handler — undefined for the current (last) segment */
  readonly onPress?: () => void;
}

/** Props for BreadcrumbView */
interface BreadcrumbViewProps {
  /** Pre-built breadcrumb segments with handlers from the Container */
  readonly segments: readonly BreadcrumbSegment[];
}

/**
 * Renders breadcrumb navigation trail
 * @param root0 - Component props
 * @param root0.segments - Pre-built segments with labels and handlers
 * @remarks
 * The first segment is wrapped in a Pressable only when its onPress handler is
 * defined. When onPress is absent (e.g. the breadcrumb has a single current
 * entry), rendering a Pressable would create a dead tap target — a touchable
 * element that does nothing — which confuses accessibility tooling and users.
 */
const BreadcrumbView = ({ segments }: BreadcrumbViewProps) =>
  segments.length > 0 ? (
    <Stack
      direction="horizontal"
      {...{ "data-testid": TEST_ID_PLAYER_DETAIL.BREADCRUMB }}
      className="items-center gap-1 pb-1"
    >
      {segments[0].onPress ? (
        <Pressable
          onPress={segments[0].onPress}
          className="flex-row items-center gap-1"
          accessibilityRole="button"
          accessibilityLabel={`Navigate back to ${segments[0].label}`}
        >
          <Icon as={ArrowLeftIcon} size="sm" className="text-content-muted" />
          <Text variant="body" className="text-content-muted">
            {segments[0].label}
          </Text>
        </Pressable>
      ) : (
        <Stack direction="horizontal" className="flex-row items-center gap-1">
          <Icon as={ArrowLeftIcon} size="sm" className="text-content-muted" />
          <Text variant="body" className="text-content-muted">
            {segments[0].label}
          </Text>
        </Stack>
      )}
      {segments.slice(1).map(segment => (
        <Stack
          direction="horizontal"
          key={segment.key}
          className="items-center gap-1"
        >
          <Text variant="body" className="text-content-subtle">
            /
          </Text>
          {segment.isCurrent ? (
            <Text variant="body" className="text-content-primary">
              {segment.label}
            </Text>
          ) : (
            <Pressable onPress={segment.onPress}>
              <Text variant="body" className="text-content-muted">
                {segment.label}
              </Text>
            </Pressable>
          )}
        </Stack>
      ))}
    </Stack>
  ) : null;

BreadcrumbView.displayName = "BreadcrumbView";

export default memo(BreadcrumbView);
